/**
 * PTEL LocalData - Servicio de carga y sincronización
 * 
 * Gestiona la carga inicial de datos DERA/INE en IndexedDB (Dexie.js).
 * Descarga desde R2 (Cloudflare) con fallback a /public/data/.
 * 
 * @module lib/localData/localDataService
 * @version 2.0.0
 * @date 2026-01-28
 * @session R2 migration with fallback
 */

import { db, type DERAFeature, type INEMunicipio, type SyncMetadata } from './schemas';
import { 
  getR2Url, 
  isR2Enabled, 
  R2_TIMEOUT,
  recordR2Hit, 
  recordR2Miss, 
  recordR2Error,
  shouldTryR2,
  recordCircuitSuccess,
  recordCircuitFailure,
  getCircuitState
} from './r2Config';
import type { Table } from 'dexie';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Estado de progreso de la carga
 */
export interface LoadProgress {
  /** Fase actual: 'checking' | 'downloading' | 'inserting' | 'complete' | 'error' */
  phase: 'checking' | 'downloading' | 'inserting' | 'complete' | 'error';
  /** Dataset actual siendo procesado */
  currentDataset: 'dera' | 'ine' | null;
  /** Porcentaje total (0-100) */
  percent: number;
  /** Registros procesados */
  processed: number;
  /** Total de registros a procesar */
  total: number;
  /** Mensaje descriptivo */
  message: string;
  /** Error si phase='error' */
  error?: Error;
}

/**
 * Opciones de configuración del servicio
 */
export interface LoadOptions {
  /** Forzar recarga aunque existan datos */
  forceReload?: boolean;
  /** Callback de progreso */
  onProgress?: (progress: LoadProgress) => void;
  /** Timeout para fetch en ms (default: 30000) */
  fetchTimeout?: number;
}

/**
 * Resultado de la carga
 */
export interface LoadResult {
  success: boolean;
  deraCount: number;
  ineCount: number;
  durationMs: number;
  error?: Error;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Base path dinámico que funciona tanto en desarrollo como en producción.
 * 
 * - Desarrollo: import.meta.env.BASE_URL = '/'
 * - Producción (pteltool.es / GitHub Pages): import.meta.env.BASE_URL = '/'
 * 
 * Eliminamos trailing slash si existe para evitar dobles barras.
 */
const VITE_BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
const BASE_PATH = `${VITE_BASE}/data`;

const DERA_FILE = 'dera-dexie/all-dera.json';
const INE_FILE = 'ine/municipios.json';
const DEFAULT_TIMEOUT = 30000;

// Tamaño de batch para inserciones (evita bloquear UI)
const BATCH_SIZE = 500;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Fetch con timeout
 */
async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      // Diagnóstico detallado para diferentes códigos de error
      const errorMessages: Record<number, string> = {
        404: 'Archivo no encontrado. Verifica que el deploy incluya los datos.',
        405: 'Método no permitido. Posible Service Worker desactualizado - limpia caché del navegador.',
        500: 'Error del servidor.',
        503: 'Servicio no disponible.'
      };
      const detail = errorMessages[response.status] || response.statusText;
      throw new Error(`HTTP ${response.status}: ${detail}`);
    }
    
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Timeout después de ${timeoutMs}ms`, { cause: error });
    }
    throw error;
  }
}

/**
 * Resultado de fetch con información de origen
 */
interface FetchResult {
  response: Response;
  source: 'r2' | 'local';
  url: string;
}

/**
 * Fetch con R2 primero, fallback a local
 * 
 * Flujo:
 * 1. Si R2 habilitado, intenta R2 (gzip)
 * 2. Si R2 falla o deshabilitado, usa local
 * 3. Registra estadísticas para debugging
 */
async function fetchWithR2Fallback(
  localFile: string,
  localUrl: string,
  timeoutMs: number
): Promise<FetchResult> {
  // Intentar R2 si está habilitado Y el Circuit Breaker lo permite
  if (isR2Enabled() && shouldTryR2()) {
    const r2Url = getR2Url(localFile);
    if (r2Url) {
      try {
        const circuitState = getCircuitState();
        const logPrefix = circuitState === 'half-open' ? '[LocalData] Testing R2 recovery:' : '[LocalData] Trying R2:';
        console.log(`${logPrefix} ${r2Url}`);
        
        const response = await fetchWithTimeout(r2Url, R2_TIMEOUT);
        
        // Éxito: registrar en estadísticas y Circuit Breaker
        recordR2Hit();
        recordCircuitSuccess();
        console.log(`[LocalData] R2 hit: ${r2Url}`);
        return { response, source: 'r2', url: r2Url };
      } catch (r2Error) {
        const errorMsg = r2Error instanceof Error ? r2Error.message : String(r2Error);
        
        // Fallo: registrar en estadísticas y Circuit Breaker
        recordR2Error(errorMsg);
        recordCircuitFailure();
        console.warn(`[LocalData] R2 failed, falling back to local: ${errorMsg}`);
      }
    }
  } else if (!shouldTryR2()) {
    // Circuit Breaker abierto - log informativo
    console.log(`[LocalData] Circuit breaker OPEN - skipping R2, using local directly`);
  }
  
  // Fallback a local
  recordR2Miss();
  console.log(`[LocalData] Using local: ${localUrl}`);
  const response = await fetchWithTimeout(localUrl, timeoutMs);
  return { response, source: 'local', url: localUrl };
}

/**
 * Parsea respuesta JSON, soportando gzip de R2
 */
async function parseJsonResponse(result: FetchResult): Promise<unknown> {
  // R2 sirve .gz pero el navegador lo descomprime automáticamente con Accept-Encoding
  // El Worker R2 ya maneja esto correctamente
  return result.response.json();
}

/**
 * Inserta registros en batches para no bloquear la UI
 */
async function bulkInsertInBatches<T>(
  table: Table<T, string>,
  records: T[],
  onBatch?: (processed: number) => void
): Promise<void> {
  for (let i = 0; i < records.length; i += BATCH_SIZE) {
    const batch = records.slice(i, i + BATCH_SIZE);
    await table.bulkPut(batch as T[]);
    onBatch?.(Math.min(i + BATCH_SIZE, records.length));
    // Pequeña pausa para permitir actualizar UI
    await new Promise(resolve => setTimeout(resolve, 0));
  }
}

/**
 * Extrae array de municipios desde diferentes formatos de respuesta INE
 * 
 * Formatos soportados:
 * - Array directo: [...]
 * - Objeto con "municipios": { municipios: [...] }
 * - Objeto con "data": { data: [...] }
 * - Objeto con "features": { features: [...] }
 * - Objeto con primer array encontrado
 */
function extractINEArray(rawData: unknown, url: string): INEMunicipio[] {
  // Caso 1: Es un array directo
  if (Array.isArray(rawData)) {
    return rawData;
  }
  
  // Caso 2: Es un objeto
  if (rawData && typeof rawData === 'object') {
    const obj = rawData as Record<string, unknown>;
    
    // Buscar propiedades conocidas que contengan arrays
    const arrayProps = ['municipios', 'data', 'features', 'items', 'records'];
    for (const prop of arrayProps) {
      if (Array.isArray(obj[prop])) {
        console.log(`[LocalData] INE parsed from {${prop}: [...]} format`);
        return obj[prop] as INEMunicipio[];
      }
    }
    
    // Buscar cualquier propiedad que sea un array
    for (const key of Object.keys(obj)) {
      if (Array.isArray(obj[key])) {
        console.log(`[LocalData] INE parsed from {${key}: [...]} format`);
        return obj[key] as INEMunicipio[];
      }
    }
    
    // Log de debug
    console.error('[LocalData] INE object has no array properties:', Object.keys(obj));
  }
  
  throw new Error(`INE: Formato inesperado. Esperado array, recibido ${typeof rawData}. URL: ${url}`);
}

// ============================================================================
// SERVICIO PRINCIPAL
// ============================================================================

/**
 * Verifica si la base de datos ya tiene datos cargados
 */
export async function checkDataLoaded(): Promise<{
  isLoaded: boolean;
  deraCount: number;
  ineCount: number;
  lastSync: string | null;
}> {
  try {
    const [deraCountRaw, ineCountRaw, syncMeta] = await Promise.all([
      db.dera.count(),
      db.ine.count(),
      db.syncMetadata.get('dera')
    ]);
    
    // Asegurar que siempre sean números válidos (fix toLocaleString undefined)
    const deraCount = typeof deraCountRaw === 'number' ? deraCountRaw : 0;
    const ineCount = typeof ineCountRaw === 'number' ? ineCountRaw : 0;
    
    return {
      isLoaded: deraCount > 0 && ineCount > 0,
      deraCount,
      ineCount,
      lastSync: syncMeta?.lastSync ?? null
    };
  } catch (error) {
    console.error('[LocalData] Error checking data:', error);
    return {
      isLoaded: false,
      deraCount: 0,
      ineCount: 0,
      lastSync: null
    };
  }
}

/**
 * Carga inicial de datos DERA e INE en IndexedDB
 * 
 * Flujo:
 * 1. Verificar si ya hay datos (skip si forceReload=false)
 * 2. Descargar JSON de DERA
 * 3. Insertar DERA en batches
 * 4. Descargar JSON de INE
 * 5. Insertar INE
 * 6. Actualizar SyncMetadata
 */
export async function loadInitialData(options: LoadOptions = {}): Promise<LoadResult> {
  const {
    forceReload = false,
    onProgress,
    fetchTimeout = DEFAULT_TIMEOUT
  } = options;
  
  const startTime = Date.now();
  let deraCount = 0;
  let ineCount = 0;
  
  const report = (progress: Partial<LoadProgress>) => {
    onProgress?.({
      phase: 'checking',
      currentDataset: null,
      percent: 0,
      processed: 0,
      total: 0,
      message: '',
      ...progress
    } as LoadProgress);
  };
  
  try {
    // Fase 1: Verificar estado actual
    report({
      phase: 'checking',
      message: 'Verificando base de datos local...'
    });
    
    const status = await checkDataLoaded();
    
    if (status.isLoaded && !forceReload) {
      report({
        phase: 'complete',
        percent: 100,
        processed: status.deraCount + status.ineCount,
        total: status.deraCount + status.ineCount,
        message: 'Datos ya cargados'
      });
      
      return {
        success: true,
        deraCount: status.deraCount,
        ineCount: status.ineCount,
        durationMs: Date.now() - startTime
      };
    }
    
    // Si forceReload, limpiar tablas primero
    if (forceReload && status.isLoaded) {
      report({
        phase: 'checking',
        message: 'Limpiando datos anteriores...'
      });
      await Promise.all([
        db.dera.clear(),
        db.ine.clear()
      ]);
    }
    
    // Fase 2: Descargar DERA
    report({
      phase: 'downloading',
      currentDataset: 'dera',
      percent: 5,
      message: 'Descargando datos DERA...'
    });
    
    const deraUrl = `${BASE_PATH}/${DERA_FILE}`;
    const deraResult = await fetchWithR2Fallback(DERA_FILE, deraUrl, fetchTimeout);
    console.log(`[LocalData] DERA loaded from ${deraResult.source}: ${deraResult.url}`);
    
    // Parsear y validar respuesta
    let deraData: DERAFeature[];
    try {
      const rawData = await parseJsonResponse(deraResult);
      
      // El archivo puede ser un array directo O un objeto con "features"
      if (Array.isArray(rawData)) {
        deraData = rawData;
      } else if (rawData && typeof rawData === 'object' && 'features' in rawData && Array.isArray((rawData as Record<string, unknown>).features)) {
        // Formato DERAFeatureCollection: { type: "...", features: [...] }
        deraData = (rawData as { features: DERAFeature[] }).features;
        console.log(`[LocalData] DERA parsed from FeatureCollection format`);
      } else {
        console.error('[LocalData] DERA response format unexpected:', typeof rawData, Object.keys((rawData as object) || {}));
        throw new Error(`DERA: Formato inesperado. Esperado array o {features:[]}, recibido ${typeof rawData}. URL: ${deraUrl}`);
      }
      
      console.log(`[LocalData] DERA parsed successfully: ${deraData.length} features`);
    } catch (parseError) {
      // Si falla el parsing, mostrar diagnóstico
      console.error('[LocalData] Error parsing DERA JSON:', parseError);
      throw new Error(`Error parseando JSON de DERA desde ${deraUrl}: ${parseError instanceof Error ? parseError.message : String(parseError)}`, { cause: parseError });
    }
    
    report({
      phase: 'downloading',
      currentDataset: 'dera',
      percent: 25,
      total: deraData.length,
      message: `DERA: ${deraData.length.toLocaleString()} infraestructuras`
    });
    
    // Fase 3: Insertar DERA
    report({
      phase: 'inserting',
      currentDataset: 'dera',
      percent: 30,
      total: deraData.length,
      message: 'Insertando datos DERA...'
    });
    
    await bulkInsertInBatches(db.dera, deraData, (processed) => {
      const percentDera = 30 + Math.floor((processed / deraData.length) * 30);
      report({
        phase: 'inserting',
        currentDataset: 'dera',
        percent: percentDera,
        processed,
        total: deraData.length,
        message: `DERA: ${processed.toLocaleString()} / ${deraData.length.toLocaleString()}`
      });
    });
    
    deraCount = deraData.length;
    
    // Fase 4: Descargar INE
    report({
      phase: 'downloading',
      currentDataset: 'ine',
      percent: 65,
      message: 'Descargando datos INE...'
    });
    
    const ineUrl = `${BASE_PATH}/${INE_FILE}`;
    const ineResult = await fetchWithR2Fallback(INE_FILE, ineUrl, fetchTimeout);
    console.log(`[LocalData] INE loaded from ${ineResult.source}: ${ineResult.url}`);
    
    // Parsear y validar respuesta INE (soporta múltiples formatos)
    let ineData: INEMunicipio[];
    try {
      const rawIneData = await parseJsonResponse(ineResult);
      ineData = extractINEArray(rawIneData, ineUrl);
      console.log(`[LocalData] INE parsed successfully: ${ineData.length} municipios`);
    } catch (parseError) {
      console.error('[LocalData] Error parsing INE JSON:', parseError);
      throw new Error(`Error parseando JSON de INE desde ${ineUrl}: ${parseError instanceof Error ? parseError.message : String(parseError)}`, { cause: parseError });
    }
    
    report({
      phase: 'downloading',
      currentDataset: 'ine',
      percent: 75,
      total: ineData.length,
      message: `INE: ${ineData.length} municipios`
    });
    
    // Fase 5: Insertar INE
    report({
      phase: 'inserting',
      currentDataset: 'ine',
      percent: 80,
      total: ineData.length,
      message: 'Insertando datos INE...'
    });
    
    await db.ine.bulkPut(ineData);
    ineCount = ineData.length;
    
    report({
      phase: 'inserting',
      currentDataset: 'ine',
      percent: 90,
      processed: ineCount,
      total: ineCount,
      message: `INE: ${ineCount} municipios insertados`
    });
    
    // Fase 6: Actualizar SyncMetadata
    const now = new Date().toISOString();
    const syncMetaDera: SyncMetadata = {
      id: 'dera',
      status: 'completed',
      lastSync: now,
      nextSync: null,
      dataVersion: '2025-12-05',
      recordCount: deraCount,
      sizeBytes: JSON.stringify(deraData).length,
      lastAttempt: now
    };
    
    const syncMetaIne: SyncMetadata = {
      id: 'ine',
      status: 'completed',
      lastSync: now,
      nextSync: null,
      dataVersion: '2025-12-05',
      recordCount: ineCount,
      sizeBytes: JSON.stringify(ineData).length,
      lastAttempt: now
    };
    
    await db.syncMetadata.bulkPut([syncMetaDera, syncMetaIne]);
    
    // Completo
    report({
      phase: 'complete',
      currentDataset: null,
      percent: 100,
      processed: deraCount + ineCount,
      total: deraCount + ineCount,
      message: `Carga completada: ${deraCount.toLocaleString()} DERA + ${ineCount} INE`
    });
    
    return {
      success: true,
      deraCount,
      ineCount,
      durationMs: Date.now() - startTime
    };
    
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    
    report({
      phase: 'error',
      message: `Error: ${errorObj.message}`,
      error: errorObj
    });
    
    // Registrar error en SyncMetadata
    try {
      await db.syncMetadata.put({
        id: 'dera',
        status: 'error',
        lastSync: null,
        nextSync: null,
        dataVersion: null,
        recordCount: 0,
        sizeBytes: 0,
        errorMessage: errorObj.message,
        lastAttempt: new Date().toISOString()
      });
    } catch {
      // Ignorar errores al guardar metadata
    }
    
    return {
      success: false,
      deraCount,
      ineCount,
      durationMs: Date.now() - startTime,
      error: errorObj
    };
  }
}

/**
 * Limpia todos los datos locales
 */
export async function clearAllData(): Promise<void> {
  await Promise.all([
    db.dera.clear(),
    db.ine.clear(),
    db.boundaries.clear(),
    db.geocodingCache.clear(),
    db.syncMetadata.clear()
  ]);
}
